import { Injectable, inject } from '@angular/core';
import {
  Firestore,
  CollectionReference,
  collection,
  doc,
  query,
  setDoc,
  updateDoc,
  where,
} from 'firebase/firestore';
import { FirebaseService } from '../../_firebase/firebase.service';
import { AuthService } from '../auth/auth.service';
import { Utilisateur } from '../../_interfaces/utilisateur';
import { Inscription } from '../../_interfaces/inscription';
import { UtilisateurService } from '../utilisateur/utilisateur.service';
import { first, firstValueFrom } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class InscriptionService {
  private authService: AuthService = inject(AuthService);
  private firebase: FirebaseService = inject(FirebaseService);
  private firestore: Firestore = this.firebase.firestore;
  private utilisateurService: UtilisateurService = inject(UtilisateurService);
  private inscriptionsCol: CollectionReference = collection(this.firestore, 'inscriptions');

  private meUid: string | null = null;
  private me: Utilisateur | null = null;

  constructor() {
    this.authService.currentUid$.subscribe((uid) => {
      this.meUid = uid;
    });
    this.authService.currentUser$.subscribe((user) => {
      this.me = user;
    });
  }

  async readAllInscriptionOfEvenement(key: string): Promise<Inscription[]> {
    const q = query(this.inscriptionsCol, where('evenement_key', '==', key));
    const snapshot = await this.firebase.getDocs(q);
    return snapshot.docs.map(d => ({ key: d.id, ...(d.data() as Omit<Inscription, 'key'>) }));
  }

  async readAllInscriptions(): Promise<Inscription[]> {
    const snapshot = await this.firebase.getDocs(this.inscriptionsCol);
    return snapshot.docs.map(d => ({ key: d.id, ...(d.data() as Omit<Inscription, 'key'>) }));
  }

  readOneInscriptionOfMeAndEvenement(evenement_key: string | null): Promise<Inscription | undefined> {
    if (!evenement_key) {
      return Promise.reject(new Error('Impossible de lire une inscription sans clé d\'événement'));
    }
    if (!this.meUid) {
      return Promise.reject(new Error('Impossible de lire les inscriptions sans être connecté'));
    }
    const composite_key = `${this.meUid}_${evenement_key}`;
    return this.readInscriptionByCompositeKey(composite_key);
  }

  async readInscriptionByCompositeKey(composite_key: string): Promise<Inscription | undefined> {
    const snapshot = await this.firebase.getDoc(doc(this.firestore, 'inscriptions', composite_key));
    if (!snapshot.exists()) return undefined;
    return { key: snapshot.id, ...(snapshot.data() as Omit<Inscription, 'key'>) };
  }

  async createInscription(utilisateur_id: string, evenement_key: string, is_present: boolean): Promise<Inscription> {
    try {
      if (!this.meUid) {
        throw new Error('Impossible de créer une inscription sans être connecté');
      }
      const user_id = utilisateur_id || this.meUid;
      const utilisateur = await firstValueFrom(this.utilisateurService.readUtilisateurByUid(user_id));

      const composite_key = `${user_id}_${evenement_key}`;
      const existing = await this.readInscriptionByCompositeKey(composite_key);
      const now = new Date().toISOString();

      const inscription: Inscription = {
        composite_key,
        nom_prenom: `${utilisateur.lastName} ${utilisateur.firstName}`,
        evenement_key,
        utilisateur_uid: user_id,
        first_date: existing?.first_date ?? now,
        last_update: now,
        is_present,
        commentaire: existing?.commentaire ?? '',
        key: composite_key
      };

      const { key, ...data } = inscription;
      await setDoc(doc(this.firestore, 'inscriptions', composite_key), data);
      return inscription;
    } catch (error) {
      console.error(error);
      throw error;
    }
  }

  updateInscription(inscription: Inscription): Promise<void> {
    if (inscription.key == null) {
      return Promise.reject('La clé de l\'inscription est nulle');
    }
    const inscriptionRef = doc(this.firestore, 'inscriptions', inscription.key);
    return updateDoc(inscriptionRef, {
      last_update: new Date().toISOString(),
      is_present: inscription.is_present,
      commentaire: inscription.commentaire
    });
  }

  async getEmailsOfUsersPresentAtEvent(eventKey: string): Promise<string[]> {
    const inscriptions = await this.readAllInscriptionOfEvenement(eventKey);
    const userKeysPresent = inscriptions
      .filter(inscription => inscription.is_present)
      .map(inscription => inscription.utilisateur_uid);

    const utilisateurs = await Promise.all(
      userKeysPresent.map(uid => firstValueFrom(this.utilisateurService.readUtilisateurByUid(uid)))
    );

    return utilisateurs
      .filter((utilisateur): utilisateur is Utilisateur => utilisateur != null)
      .map(utilisateur => utilisateur.email)
      .filter((email): email is string => email != null);
  }

  async getEmailsOfUsersNotInscribedToEvent(eventKey: string): Promise<string[]> {
    const allUtilisateurs = await firstValueFrom(this.utilisateurService.readAllUtilisateurs().pipe(first()));
    if (!allUtilisateurs) return [];

    const inscriptions = await this.readAllInscriptionOfEvenement(eventKey);
    const userKeysInEvent = new Set(inscriptions.map(inscription => inscription.utilisateur_uid));

    return allUtilisateurs
      .filter(utilisateur => !userKeysInEvent.has(utilisateur.uid))
      .map(utilisateur => utilisateur.email);
  }
}
